import { zodResolver } from '@hookform/resolvers/zod';
import { useRecaptchaMutation } from '@hub/app/hooks/useRecaptchaMutation';
import { extractApiErrorCode } from '@hub/lib/api-error/extract-api-error-code';
import { SELF_REPORTED_ERROR_META } from '@hub/lib/queryMeta';
import { isRecaptchaBlocked } from '@hub/lib/recaptcha';
import { m } from '@hub/messages';
import { ApiError, reAuthenticateWithRecaptcha } from '@platform/api';
import { Button, Form, FormPasswordField, FormSubmitButton, type FormSubmitHandler, HiddenUsernameField } from '@platform/ui';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type StepUpPasswordCode = 'invalid-credentials' | 'account-locked' | 'legal-block';

const STEP_UP_PASSWORD_CODES: ReadonlySet<StepUpPasswordCode> = new Set(['invalid-credentials', 'account-locked', 'legal-block']);

interface PasswordFormProps {
  email: string;
  error: string | null;
  setError: (e: string | null) => void;
  onSuccess: (token: string, expiresAt: string) => void;
  onBack?: () => void;
  onCancel: () => void;
}

const passwordFormSchema = z.object({
  password: z.string().min(1, { error: () => m.auth_mfa_error_password_required() }),
});
type FormValues = z.infer<typeof passwordFormSchema>;

export function PasswordForm({ email, error, setError, onSuccess, onBack, onCancel }: PasswordFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { password: '' },
  });

  const { mutateAsync, isPending } = useRecaptchaMutation(
    'reAuthenticate',
    'step_up_password',
    async ({ password }: FormValues, { recaptcha }) => {
      const { data } = await reAuthenticateWithRecaptcha({ body: { password }, meta: { recaptcha }, throwOnError: true });
      return data;
    },
    {
      onSuccess: ({ stepUpToken, expiresAt }) => onSuccess(stepUpToken, expiresAt),
      onError: (err) => {
        if (isRecaptchaBlocked(err)) {
          setError(m.auth_mfa_error_generic());
          return;
        }
        const code = extractApiErrorCode<StepUpPasswordCode>(err, {
          flow: 'step_up_password',
          byCode: STEP_UP_PASSWORD_CODES,
        });
        switch (code) {
          case 'invalid-credentials':
            form.setError('password', { type: 'server', message: m.auth_mfa_error_wrong_password() }, { shouldFocus: true });
            break;
          case 'account-locked':
            setError(m.auth_mfa_error_account_locked());
            break;
          case 'legal-block':
            setError(m.auth_mfa_error_legal());
            break;
          default:
            setError(err instanceof ApiError ? m.auth_mfa_error_generic() : m.auth_mfa_error_network());
        }
      },
      meta: SELF_REPORTED_ERROR_META,
    },
  );

  const onSubmit: FormSubmitHandler<FormValues> = async ({ password }) => {
    setError(null);
    form.clearErrors('password');
    await mutateAsync({ password });
  };

  return (
    <Form form={form} onSubmit={onSubmit}>
      <div className="space-y-4">
        <HiddenUsernameField username={email} label={m.identity_username_field_label()} />

        <FormPasswordField name="password" label={m.auth_mfa_password_label()} autoComplete="current-password" autoFocus />

        {error != null && (
          <p id="mfa-error" className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onBack ?? onCancel} disabled={isPending}>
            {onBack ? m.auth_mfa_back_to_selection() : m.auth_mfa_cancel_button()}
          </Button>
          <FormSubmitButton className="flex-1">{m.auth_mfa_submit_button()}</FormSubmitButton>
        </div>
      </div>
    </Form>
  );
}
